import { Injectable } from '@angular/core';
import { BpiType } from '@shared/core';
import { BaseMapper } from '@shared/data';
import {
    BookingPlaceholderDto,
    MaskedContractorDto,
    MaskedOrgUnitDto,
    MaskedServiceDto,
    MaskedSubcontractorDto,
    MaskedTimeslotPlaceholdersResponseDto,
    ServiceConfigurationDto,
} from '../dto';
import {
    BookingPlaceholder,
    MaskedContractor,
    MaskedOrgUnit,
    MaskedService,
    MaskedSubcontractor,
    MaskedTimeslotPlaceholdersResponse,
    ServiceConfiguration,
} from '../model';

@Injectable({
    providedIn: 'root',
})
export class MaskedTimeslotPlaceholdersMapper extends BaseMapper<MaskedTimeslotPlaceholdersResponseDto, MaskedTimeslotPlaceholdersResponse> {
    mapDtoToModel(dto: MaskedTimeslotPlaceholdersResponseDto): MaskedTimeslotPlaceholdersResponse {
        return {
            waitingQueue: dto.waitingQueue?.map(item => this.mapBookingPlaceholderDtoToModel(item)) ?? [],
            directBooking: dto.directBooking?.map(item => this.mapBookingPlaceholderDtoToModel(item)) ?? [],
            details: {
                contractors: dto.details?.contractors?.map(item => this.mapMaskedContractorDtoToModel(item)) ?? [],
                subcontractors: dto.details?.subcontractors?.map(item => this.mapMaskedSubcontractorDtoToModel(item)) ?? [],
                orgUnits: dto.details?.orgUnits?.map(item => this.mapMaskedOrgUnitDtoToModel(item)) ?? [],
                services: dto.details?.services?.map(item => this.mapMaskedServiceDtoToModel(item)) ?? [],
            },
            serviceConfigurations: dto.serviceConfigurations?.map(item => this.mapServiceConfigurationDtoToModel(item)) ?? [],
        };
    }

    private mapBookingPlaceholderDtoToModel(dto: BookingPlaceholderDto): BookingPlaceholder {
        return {
            maskedContractorId: dto.maskedContractorId ?? '',
            maskedSubcontractorId: dto.maskedSubcontractorId ?? '',
            maskedServiceId: dto.maskedServiceId ?? '',
            maskedOrgUnitId: dto.maskedOrgUnitId,
        };
    }

    private mapMaskedContractorDtoToModel(dto: MaskedContractorDto): MaskedContractor {
        return {
            name: dto.name ?? '',
            address: dto.address,
            post: dto.post ?? '',
            postNumber: dto.postNumber ?? '',
            phone: dto.phone ?? '',
            email: dto.email ?? '',
            directBooking: dto.directBooking ?? false,
            directTriage: dto.directTriage ?? false,
            priorityBooking: dto.priorityBooking ?? false,
            enabledInAssist: dto.enabledInAssist ?? false,
            bpiType: dto.bpiType as BpiType,
            isTesting: dto.isTesting ?? false,
            maskedId: dto.maskedId ?? '',
            maskedExternalId: dto.maskedExternalId ?? '',
            maskedCinfoId: dto.maskedCinfoId ?? '',
            maskedMedifitId: dto.maskedMedifitId ?? '',
        };
    }

    private mapMaskedSubcontractorDtoToModel(dto: MaskedSubcontractorDto): MaskedSubcontractor {
        return {
            name: dto.name ?? '',
            subspecializations: dto.subspecializations,
            maskedId: dto.maskedId ?? '',
            maskedExternalId: dto.maskedExternalId ?? '',
            maskedMedifitId: dto.maskedMedifitId ?? '',
        };
    }

    private mapMaskedOrgUnitDtoToModel(dto: MaskedOrgUnitDto): MaskedOrgUnit {
        return {
            externalId: dto.externalId ?? '',
            name: dto.name ?? '',
            addressName: dto.addressName ?? '',
            addressNumber: dto.addressNumber ?? '',
            post: dto.post ?? '',
            postNumber: dto.postNumber ?? '',
            phone: dto.phone ?? '',
            email: dto.email ?? '',
            maskedId: dto.maskedId ?? '',
            maskedContractorId: dto.maskedContractorId ?? '',
            maskedExternalId: dto.maskedExternalId ?? '',
        };
    }

    private mapMaskedServiceDtoToModel(dto: MaskedServiceDto): MaskedService {
        return {
            name: dto.name ?? '',
            directBooking: dto.directBooking,
            hasQueue: dto.hasQueue,
            subcontractorMandatory: dto.subcontractorMandatory ?? false,
            orgUnitMandatory: dto.orgUnitMandatory ?? false,
            price: dto.price ?? 0,
            maskedId: dto.maskedId ?? '',
            maskedContractorId: dto.maskedContractorId ?? '',
            maskedExternalId: dto.maskedExternalId ?? '',
            maskedInsuranceId: dto.maskedInsuranceId,
            maskedBsServiceId: dto.maskedBsServiceId,
            maskedMedifitId: dto.maskedMedifitId,
        };
    }

    private mapServiceConfigurationDtoToModel(dto: ServiceConfigurationDto): ServiceConfiguration {
        return {
            telemed: dto.telemed ?? false,
            phoneCall: dto.phoneCall ?? false,
            isFollowUpExamination: dto.isFollowUpExamination ?? false,
            maskedServiceId: dto.maskedServiceId ?? '',
        };
    }
}
